import {useEffect, useState, useMemo, useRef} from "react";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import DownloadIcon from "@mui/icons-material/Download";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFSelectNew from "@/components/Input/SelectNew";
import EmptyData from "@/components/EmptyData";

import {PlayerAPI} from "@/api/player";
import {PlayerValuationAPI} from "@/api/player_valuation";

import {getPlayerStructure} from "@/utils/functions";
import {
	initialValuationParameters,
	initialBaseValuationParameters,
} from "@/utils/constants";
import {useCategories} from "@/utils/hooks";
import {ERROR_OPERATION, SUCCESSFUL_OPERATION} from "@/utils/constants";

import PlayerRadarChart from "@/components/PlayerRadarChart";
import {generateComparisonPDF} from "@/utils/generateComparisonPDF";

import "./index.scss";

const MAX_PLAYERS = 2;
const COLORS = ["#1059bf", "#22c55e"]; // Blue, Green

function PlayersComparisonPage(props) {
	const {handleNotification} = props;
	const {t} = useTranslation();

	const seasonId = useSelector((state) => state?.season?.id);
	const {categories} = useCategories({
		setLoading: false,
		handleNotification,
	});

	const [loading, setLoading] = useState(false);
	const [pdfLoading, setPdfLoading] = useState(false);
	const [players, setPlayers] = useState([]);
	const [selectedPlayers, setSelectedPlayers] = useState([]);
	const [playersData, setPlayersData] = useState({});
	const [valuationsDataMap, setValuationsDataMap] = useState({});

	// Refs per i grafici
	const individualChartRefs = useRef({});
	const combinedChartRef = useRef(null);

	// Recupera lista giocatori
	const getPlayers = async () => {
		setLoading(true);
		try {
			const response = await PlayerAPI.list({
				seasonId,
				prospect: false,
				status: "Interno",
			});
			// Filtra solo giocatori interni (non prospect)
			const internalPlayers = response.filter((p) => !p.prospect);
			setPlayers(internalPlayers);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getPlayers();
	}, [seasonId]);

	// Formatta giocatori per select
	const formattedPlayers = useMemo(() => {
		return players
			.map((p) => ({
				value: p.id,
				label: `${p.firstName} ${p.lastName}${p.playerCategory?.name ? ` (${p.playerCategory.name})` : ""}`,
				sortKey: `${p.lastName} ${p.firstName}`,
			}))
			.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
	}, [players]);

	// Recupera dati giocatore e valutazioni
	const fetchPlayerData = async (playerId) => {
		try {
			const playerData = await PlayerAPI.getById(playerId, {seasonId});
			const valuations = await PlayerValuationAPI.playerList(playerId, {
				seasonId,
			});

			// Recupera i dati completi per ogni valutazione (come nella pagina Player)
			const valuationsData = [];
			if (valuations?.length) {
				const promises = valuations.map((v) => {
					return PlayerValuationAPI.getById(v.id);
				});
				const responses = await Promise.all(promises);
				valuationsData.push(...responses);
			}

			return {playerData, valuationsData};
		} catch (error) {
			console.error(`Error fetching data for player ${playerId}:`, error);
			return {playerData: null, valuationsData: []};
		}
	};

	// Gestisce selezione giocatori
	const handlePlayerSelect = async (index, selectedValue) => {
		const newSelectedPlayers = [...selectedPlayers];

		if (selectedValue) {
			if (newSelectedPlayers[index]?.value === selectedValue.value) {
				return; // Giocatore già selezionato
			}

			// Rimuovi se già selezionato in un'altra posizione
			const existingIndex = newSelectedPlayers.findIndex(
				(p) => p?.value === selectedValue.value,
			);
			if (existingIndex !== -1) {
				newSelectedPlayers[existingIndex] = null;
			}

			newSelectedPlayers[index] = selectedValue;
		} else {
			newSelectedPlayers[index] = null;
		}

		// Rimuovi null alla fine
		while (
			newSelectedPlayers.length > 0 &&
			newSelectedPlayers[newSelectedPlayers.length - 1] === null
		) {
			newSelectedPlayers.pop();
		}

		setSelectedPlayers(newSelectedPlayers);

		// Recupera dati per i nuovi giocatori selezionati
		if (selectedValue) {
			setLoading(true);
			try {
				const {playerData, valuationsData} = await fetchPlayerData(
					selectedValue.value,
				);

				setPlayersData((prev) => ({
					...prev,
					[selectedValue.value]: playerData,
				}));

				setValuationsDataMap((prev) => ({
					...prev,
					[selectedValue.value]: valuationsData,
				}));
			} catch (error) {
				handleNotification(ERROR_OPERATION, "error");
			} finally {
				setLoading(false);
			}
		}
	};

	// Rimuove giocatore
	const handleRemovePlayer = (index) => {
		const newSelectedPlayers = [...selectedPlayers];
		const removedPlayer = newSelectedPlayers[index];
		newSelectedPlayers.splice(index, 1);

		// Rimuovi null alla fine
		while (
			newSelectedPlayers.length > 0 &&
			newSelectedPlayers[newSelectedPlayers.length - 1] === null
		) {
			newSelectedPlayers.pop();
		}

		setSelectedPlayers(newSelectedPlayers);

		if (removedPlayer) {
			setPlayersData((prev) => {
				const newData = {...prev};
				delete newData[removedPlayer.value];
				return newData;
			});
			setValuationsDataMap((prev) => {
				const newData = {...prev};
				delete newData[removedPlayer.value];
				return newData;
			});
		}
	};

	// Prepara dati per radar chart combinato
	const combinedRadarData = useMemo(() => {
		if (selectedPlayers.length < 2) return [];

		// Calcola radarData per ogni giocatore usando la stessa logica di PlayerRadarChart
		const allRadarData = {};
		selectedPlayers.forEach((player) => {
			if (
				player &&
				playersData[player.value] &&
				valuationsDataMap[player.value]?.length
			) {
				const playerData = playersData[player.value];
				const valuationsData = valuationsDataMap[player.value];
				const avgAreas = {};

				const playerCategory = categories?.filter(
					(c) => c.id === playerData?.playerCategoryId,
				)?.[0];

				valuationsData.forEach((v) => {
					const parameters =
						!playerCategory?.basicActivities ||
						playerData?.playerCategoryName === "Under 13"
							? initialValuationParameters(t)
							: initialBaseValuationParameters(t);

					parameters.forEach((p) => {
						if (!avgAreas[p.area]) {
							avgAreas[p.area] = {};
						}

						avgAreas[p.area].total =
							(avgAreas[p.area].total || 0) + (v[p.fieldKeyRating] || 0);
						avgAreas[p.area].elements =
							(avgAreas[p.area].elements || 0) + (v[p.fieldKeyRating] ? 1 : 0);
					});
				});

				const radarData = [];
				Object.keys(avgAreas).forEach((area) => {
					radarData.push({
						subject:
							String(area).charAt(0).toUpperCase() + String(area).slice(1),
						A: avgAreas[area]?.elements
							? (avgAreas[area]?.total || 0) / avgAreas[area]?.elements
							: 0,
						fullMark: 10,
					});
				});

				radarData.push({
					subject: "Struttura",
					A: getPlayerStructure(
						playerData?.playerCategoryName || "",
						playerData?.lastHeight,
						playerData?.lastWeight,
					),
					fullMark: 10,
				});

				allRadarData[player.value] = radarData;
			}
		});

		// Raccogli tutti i subject unici
		const allSubjects = new Set();
		Object.values(allRadarData).forEach((radarData) => {
			radarData.forEach((item) => {
				allSubjects.add(item.subject);
			});
		});

		// Crea array combinato
		const combined = Array.from(allSubjects).map((subject) => {
			const dataPoint = {subject, fullMark: 10};
			selectedPlayers.forEach((player, index) => {
				if (player && allRadarData[player.value]) {
					const playerData = allRadarData[player.value].find(
						(item) => item.subject === subject,
					);
					dataPoint[`player${index + 1}`] = playerData?.A || 0;
				}
			});
			return dataPoint;
		});

		return combined;
	}, [selectedPlayers, playersData, valuationsDataMap, categories, t]);

	// Gestisce il download del PDF
	const handleDownloadPDF = async () => {
		if (selectedPlayers.length === 0) {
			handleNotification("Seleziona almeno un giocatore", "warning");
			return;
		}

		setPdfLoading(true);
		try {
			const pdfGenerator = await generateComparisonPDF(
				selectedPlayers,
				playersData,
				valuationsDataMap,
				combinedRadarData,
				t,
			);

			// Aggiungi i grafici individuali
			for (let i = 0; i < selectedPlayers.length; i++) {
				const chartElement =
					individualChartRefs.current[selectedPlayers[i]?.value];
				if (chartElement) {
					await pdfGenerator.addIndividualChart(chartElement, i);
				}
			}

			// Aggiungi il grafico combinato se presente
			if (selectedPlayers.length > 1 && combinedChartRef.current) {
				await pdfGenerator.addCombinedChart(combinedChartRef.current);
			}

			// Salva il PDF
			pdfGenerator.save();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			console.error("Errore nella generazione del PDF:", error);
			handleNotification(
				error.message || "Errore nella generazione del PDF",
				"error",
			);
		} finally {
			setPdfLoading(false);
		}
	};

	const headerActions = [
		{
			icon: <DownloadIcon />,
			title: pdfLoading ? "Generazione PDF..." : "Scarica PDF",
			onClick: handleDownloadPDF,
		},
	];

	return (
		<Page className="players-comparison-page">
			<Loading visible={loading} light />
			<Breadcrumbs actions={<MainActions actions={headerActions} />} />
			<div className="row no-margin col-container mt-2">
				<div className="col-12 padding-small">
					<VLFCard
						openable={false}
						title=""
						content={
							<div className="comparison-container">
								<div className="players-selector">
									<div className="selector-title">
										Seleziona fino a {MAX_PLAYERS} giocatori da confrontare
									</div>
									<div className="row no-margin">
										{Array.from({length: MAX_PLAYERS}).map((_, index) => (
											<div
												key={index}
												className="col-12 col-md-6 padding-small"
											>
												<div className="player-select-item">
													<VLFSelectNew
														options={formattedPlayers}
														sortKey="sortKey"
														value={selectedPlayers[index] || null}
														onChange={(value) =>
															handlePlayerSelect(index, value)
														}
														label={`Giocatore ${index + 1}`}
														isClearable
													/>
												</div>
											</div>
										))}
									</div>
								</div>

								{selectedPlayers.length > 0 && (
									<>
										{/* Radar Chart Individuali */}
										<div className="individual-charts">
											<div className="row no-margin">
												{selectedPlayers.map((player, index) => {
													if (!player) return null;
													const playerData = playersData[player.value];
													const valuationsData =
														valuationsDataMap[player.value] || [];

													if (!playerData || !valuationsData.length) {
														return (
															<div
																key={player.value}
																className="col-12 col-md-6 padding-small"
															>
																<VLFCard
																	openable={false}
																	title={
																		player.label?.split(" (")[0] || "Giocatore"
																	}
																	content={<EmptyData />}
																/>
															</div>
														);
													}

													return (
														<div
															key={player.value}
															className="col-12 col-md-6 padding-small"
														>
															<VLFCard
																openable={false}
																title={
																	player.label?.split(" (")[0] || "Giocatore"
																}
																content={
																	<div
																		className="chart-wrapper-for-pdf"
																		ref={(el) => {
																			if (el) {
																				individualChartRefs.current[
																					player.value
																				] =
																					el.querySelector(
																						".player-radar-chart-container",
																					) || el;
																			}
																		}}
																	>
																		<PlayerRadarChart
																			playerData={playerData}
																			valuationsData={valuationsData}
																			color={COLORS[index]}
																		/>
																	</div>
																}
															/>
														</div>
													);
												})}
											</div>
										</div>

										{/* Radar Chart Combinato */}
										{selectedPlayers.length > 1 &&
											combinedRadarData.length > 0 && (
												<div className="combined-chart">
													<div className="row no-margin">
														<div className="col-12 padding-small">
															<VLFCard
																title="Radar Chart Comparativo"
																content={
																	<div
																		className="chart-wrapper-for-pdf"
																		ref={(el) => {
																			if (el) {
																				combinedChartRef.current =
																					el.querySelector(
																						".player-radar-chart-container",
																					) || el;
																			}
																		}}
																	>
																		<PlayerRadarChart
																			multiPlayerData={combinedRadarData}
																			players={selectedPlayers.map((p, i) => ({
																				name:
																					p?.label?.split(" (")[0] ||
																					`Giocatore ${i + 1}`,
																				color: COLORS[i],
																			}))}
																		/>
																	</div>
																}
															/>
														</div>
													</div>
												</div>
											)}
									</>
								)}
							</div>
						}
					/>
				</div>
			</div>
		</Page>
	);
}

export default PlayersComparisonPage;
